import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { FileText, User, Phone, ExternalLink, Package, Home } from 'lucide-react';
import { formatarTelefone } from '@/utils/formatters';

interface LeadPedidosTabProps {
  leadId: string;
  pedidoId?: string | null;
  onOpenPedido: (pedidoId: string) => void;
}

export function LeadPedidosTab({ leadId, pedidoId, onOpenPedido }: LeadPedidosTabProps) {
  // Buscar TODOS os pedidos vinculados ao lead
  const { data: pedidosDoLead, isLoading: loadingPedidos } = useQuery({
    queryKey: ['lead-pedidos', leadId],
    queryFn: async () => {
      if (!leadId) return [];
      const { data, error } = await supabase
        .from('pedidos')
        .select('id, numero_pedido, created_at')
        .eq('lead_origem_id', leadId)
        .order('created_at');
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!leadId,
  });

  // IDs de todos os pedidos encontrados (ou fallback para pedidoId antigo)
  const pedidoIds = pedidosDoLead?.map(p => p.id) || (pedidoId ? [pedidoId] : []);

  // Buscar itens de TODOS os pedidos encontrados
  const { data: itensPedido, isLoading: loadingItens } = useQuery({
    queryKey: ['lead-itens-pedido', pedidoIds],
    queryFn: async () => {
      if (pedidoIds.length === 0) return [];
      const { data, error } = await supabase
        .from('v_planilha_mae')
        .select('pedido_id, numero_pedido, item_id, item_seq, quadro_atual, coluna_atual, status_nome, gestor_nome, gestor_whatsapp, cessionario_nome, cessionario_whatsapp, produto, item_familia')
        .in('pedido_id', pedidoIds);
      
      if (error) throw error;
      return data || [];
    },
    enabled: pedidoIds.length > 0,
  });

  const isLoading = loadingPedidos || loadingItens;

  // Agrupar itens por pedido
  const itensPorPedido = (itensPedido || []).reduce((acc, item) => {
    const key = item.pedido_id;
    if (!acc[key]) {
      acc[key] = {
        pedidoId: key,
        numeroPedido: String(item.numero_pedido || ''),
        itens: [],
        familia: null as string | null,
      };
    }
    acc[key].itens.push(item);
    // Determinar família do pedido baseado nos itens
    if (item.item_familia) {
      const familiaMovel = ['PN', 'HA', 'TT MÓVEL', 'TT_MOVEL'].some(f => 
        item.item_familia?.toUpperCase().includes(f.replace(' ', '_')) || 
        item.item_familia?.toUpperCase().includes(f.replace('_', ' '))
      );
      acc[key].familia = familiaMovel ? 'MÓVEL' : 'FIXA';
    }
    return acc;
  }, {} as Record<string, { pedidoId: string; numeroPedido: string; itens: typeof itensPedido; familia: string | null }>);

  const pedidosAgrupados = Object.values(itensPorPedido);

  if (!leadId && !pedidoId) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-muted-foreground">Nenhum pedido vinculado</p>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-4 space-y-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (pedidosAgrupados.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-muted-foreground">Nenhum pedido encontrado</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {pedidosAgrupados.map((pedido) => {
        const isMovel = pedido.familia === 'MÓVEL';
        const FamiliaIcon = isMovel ? Package : Home;
        
        return (
          <Card key={pedido.pedidoId}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2">
                  <FamiliaIcon className="w-4 h-4" />
                  <span>Pedido #{pedido.numeroPedido}</span>
                  {pedido.familia && (
                    <Badge variant={isMovel ? "default" : "secondary"} className="ml-2">
                      {pedido.familia}
                    </Badge>
                  )}
                </CardTitle>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => onOpenPedido(pedido.pedidoId)} 
                  className="gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  Abrir 360
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {pedido.itens.length > 0 ? (
                pedido.itens.map((item) => (
                  <div key={item.item_id} className="border rounded-lg p-3 space-y-2">
                    {/* Linha 1: Item Seq + Status */}
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Item #{item.item_seq}</span>
                      <Badge variant="outline">
                        {item.status_nome || 'Sem status'}
                      </Badge>
                    </div>

                    {/* Linha 2: Quadro + Coluna */}
                    <p className="text-sm text-muted-foreground">
                      {item.quadro_atual} → {item.coluna_atual}
                    </p>

                    {/* Linha 3: Produto */}
                    {item.produto && (
                      <p className="text-sm">
                        <span className="text-muted-foreground">Produto:</span> {item.produto}
                        {item.item_familia && <span className="text-muted-foreground"> ({item.item_familia})</span>}
                      </p>
                    )}

                    {/* Gestor */}
                    {item.gestor_nome && (
                      <div className="flex items-center gap-2 text-sm bg-muted/50 rounded p-2">
                        <User className="w-4 h-4 text-muted-foreground" />
                        <span className="font-medium">{item.gestor_nome}</span>
                        {item.gestor_whatsapp && (
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <Phone className="w-3 h-3" />
                            {formatarTelefone(item.gestor_whatsapp)}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Cessionário */}
                    {item.cessionario_nome && (
                      <div className="flex items-center gap-2 text-sm bg-accent/50 rounded p-2">
                        <User className="w-4 h-4 text-primary" />
                        <span className="font-medium">{item.cessionario_nome}</span>
                        {item.cessionario_whatsapp && (
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <Phone className="w-3 h-3" />
                            {formatarTelefone(item.cessionario_whatsapp)}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground text-center py-4">Nenhum item encontrado</p>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
